import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BENCHMARK_BOOK_ENTRIES,
  CATEGORY_MODIFIERS,
  GALLERY_CATEGORY_ORDER,
  type GalleryCategory,
} from '../../benchmarkBook';
import {
  computeGallery,
  DEFAULT_GALLERY_INPUTS,
  galleryTemplatePrefill,
  measurementPlan,
  pctWhole,
  type GalleryInputs,
  type GalleryTemplateId,
  type GalleryTemplatePrefill,
  type PlanRow,
} from '../../pricingGallery';
import { EXPERIMENTS_TEST_IDS } from '../../experimentsTestIds';
import { ClaimChip } from './ClaimChip';

/**
 * M0 cold-start gallery (Phase 5, mock
 * .design/pricing-loop-v2/mockups/m0-gallery-default.html, re-checked 2026-09-04
 * in DESIGN_BRIEF.md): three prefilled inputs (FP-8 confirm-not-form) → menu
 * templates ranked on benchmark-book priors through the kernel → measurement
 * plan at the expected traffic → THE one contained primary at the END.
 *
 * Nothing here reads the warehouse or writes anywhere. P6 (AC-032.1) made the
 * empty-registry primary a real ACTION: it opens the create-products confirm
 * (rendered by the parent, which owns the Stripe/Firestore bridging) instead of
 * navigating. The `count > 0` branch is untouched — it still navigates to the
 * paywall-products page (AC-030.6).
 */

export interface PricingGalleryRegistryInfo {
  hasRegisteredProducts: boolean;
  count: number;
}

export interface PricingGallerySectionProps {
  projectId: string;
  /** Reported by the calculator once the paywall-products registry loaded; null = unknown yet. */
  registry: PricingGalleryRegistryInfo | null;
  /**
   * AC-032.1: the empty-registry primary. The parent opens the confirm dialog —
   * this section stays free of firebase-core / stripe-connect.
   */
  onCreateProducts: (prefill: GalleryTemplatePrefill, categoryLabel: string) => void;
  /**
   * The anchor this section's ladder is derived from, reported upward.
   *
   * The calculator's empty-registry presets offer the SAME templates and create
   * the SAME real Stripe prices, so the two must be priced from one anchor —
   * otherwise one tab offers «Classic» at two prices (review round 1, finding
   * 3). It reports `result.ladder.anchor`, not the raw input: that is the
   * number every card here is priced from, book default included.
   */
  onAnchorChange?: (anchorMonthly: number) => void;
  /**
   * The template this section currently recommends, reported upward.
   *
   * The cards above badge it «Picked by default» (or «Picked» once the reader
   * has chosen), and it is the ranking's own answer — `rows[0]` is sorted by
   * simulated P50. The calculator's preset row offers the same four menus
   * through the same create confirm, so the two doors must emphasise the same
   * one: a tab that badges «Two tiers» here and contains «Classic» there
   * recommends two different price ladders in one screen (tier-3 REVIEW,
   * finding M4). Reported as a primitive id, like the anchor above it.
   */
  onPickChange?: (templateId: GalleryTemplateId) => void;
}

const usd = (value: number): string => `$${value.toFixed(2)}`;

function planDate(row: PlanRow): React.ReactNode {
  if (row.kind === 'month') return `month ${row.month}`;
  if (row.kind === 'unavailable') {
    return (
      <Typography variant="caption" color="text.secondary" component="span">
        n/a · {row.reason}
      </Typography>
    );
  }
  const single = row.band[0] === row.band[1];
  return (
    <>
      <span>{single ? `day ${row.day}` : `≈ day ${row.day}`}</span>
      {!single && (
        <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
          band day {row.band[0]}–{row.band[1]}
        </Typography>
      )}
    </>
  );
}

export const PricingGallerySection: React.FC<PricingGallerySectionProps> = ({ projectId, registry, onCreateProducts, onAnchorChange, onPickChange }) => {
  const [inputs, setInputs] = useState<GalleryInputs>(DEFAULT_GALLERY_INPUTS);
  const [picked, setPicked] = useState<GalleryTemplateId | null>(null);

  // Pure + deterministic (seed = hash of the inputs): recomputing per keystroke is cheap
  // (2,000 joint draws × 4 policies) and needs no debounce.
  const result = useMemo(() => computeGallery(inputs), [inputs]);
  const pickedRow = result.rows.find(row => row.id === picked) ?? result.rows[0];
  const plan = useMemo(() => measurementPlan(inputs, pickedRow), [inputs, pickedRow]);
  const trafficLabel = inputs.trafficPerDay > 0 ? inputs.trafficPerDay.toLocaleString('en-US') : '—';
  /* A primitive out, on every change of the derived anchor — the parent stores
     a number, so nothing here can hand a consumer an object that rebuilds. */
  const anchor = result.ladder.anchor;
  useEffect(() => { onAnchorChange?.(anchor); }, [anchor, onAnchorChange]);
  /* The id this section badges, out on every change of it — a primitive again,
     for the same reason, and read off `pickedRow` so the badge and the report
     can never name two templates (finding M4). */
  const pickedId = pickedRow.id;
  useEffect(() => { onPickChange?.(pickedId); }, [pickedId, onPickChange]);
  const productsHref = `/project/${projectId}/stripe/products`;
  const registryHasProducts = Boolean(registry?.hasRegisteredProducts);

  const setNumber = (key: 'anchorMonthly' | 'trafficPerDay') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number.parseFloat(event.target.value);
    setInputs(current => ({ ...current, [key]: Number.isFinite(value) ? value : 0 }));
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid={EXPERIMENTS_TEST_IDS.pricingGallery}>
      <Typography variant="subtitle1" sx={{ mb: 0.5 }}>Starting prices</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '78ch' }}>
        Nothing exists yet — that is fine. Confirm three prefilled answers, pick a menu template,
        and only then create the products. Every number is a benchmark prior until your own traffic arrives.
      </Typography>

      {/* Three prefilled inputs — numbers and one picker, no free text (rule 0, AC-030.1). */}
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" component="label" htmlFor="pricing-gallery-category">Category</Typography>
          <Select
            id="pricing-gallery-category"
            size="small"
            value={inputs.category}
            inputProps={{ 'data-testid': EXPERIMENTS_TEST_IDS.pricingGalleryCategory }}
            onChange={event => setInputs(current => ({ ...current, category: event.target.value as GalleryCategory }))}
            sx={{ minWidth: 200 }}
          >
            {GALLERY_CATEGORY_ORDER.map(category => (
              <MenuItem key={category} value={category}>{CATEGORY_MODIFIERS[category].label}</MenuItem>
            ))}
          </Select>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">Monthly anchor price</Typography>
          <TextField
            size="small"
            type="number"
            value={inputs.anchorMonthly}
            inputProps={{ min: 0.99, step: 1, 'data-testid': EXPERIMENTS_TEST_IDS.pricingGalleryAnchor, 'aria-label': 'Monthly anchor price' }}
            sx={{ width: 110 }}
            onChange={setNumber('anchorMonthly')}
          />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">Expected traffic, starts / day</Typography>
          <TextField
            size="small"
            type="number"
            value={inputs.trafficPerDay}
            inputProps={{ min: 0, step: 100, 'data-testid': EXPERIMENTS_TEST_IDS.pricingGalleryTraffic, 'aria-label': 'Expected traffic, starts per day' }}
            sx={{ width: 120 }}
            onChange={setNumber('trafficPerDay')}
          />
        </Stack>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        the price ladder derives from the anchor — annual −50%, weekly ×0.35, premium ×1.75, rounded .99 · category: {result.categoryNote}
      </Typography>

      {/* Review A-F15 (owner-delegated): ONE prior-based chip for the whole ranking;
          each card names its own book entries in the tooltip of its title. */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2" component="h3">Menu templates, ranked on priors</Typography>
        <Tooltip title={`Benchmark book v1 (${result.bookVersion}) — every card stands on editorial priors until your own traffic arrives; hover a template name for its entries`}>
          <ClaimChip tone="scenario" label="prior-based" data-testid={EXPERIMENTS_TEST_IDS.pricingGalleryChip('all')} />
        </Tooltip>
      </Stack>
      {/* Review A-F2: the caveat precedes the verdict — the overlap line sits under the heading, not after the cards. */}
      {result.overlap && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, maxWidth: '80ch' }} data-testid={EXPERIMENTS_TEST_IDS.pricingGalleryOverlap}>
          {result.overlap.line}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        value = $ per paywall user over 12 months (kernel P50, band P10–P90); «beats the classic» = share of simulated draws
      </Typography>
      {/* Review B-3: fixed column counts per breakpoint — 2×2 up to xl, 4-in-a-row from xl —
          so four templates never leave an orphan card (auto-fit gave 3+1 at 1280). */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }, gap: 2, mb: 1 }}>
        {result.rows.map(row => {
          const isPicked = row.id === pickedRow.id;
          const entries = row.priorEntries.map(id => `${id} ${BENCHMARK_BOOK_ENTRIES[id]?.title ?? ''}`.trim()).join(' · ');
          return (
            <Box
              key={row.id}
              data-testid={EXPERIMENTS_TEST_IDS.pricingGalleryCard(row.id)}
              sx={{
                border: isPicked ? 2 : 1,
                borderColor: isPicked ? 'primary.main' : 'divider',
                borderRadius: 1.5,
                p: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              <Tooltip title={`Benchmark book v1 — ${entries}`}>
                <Typography variant="body2" fontWeight={600} component="p" sx={{ cursor: 'help', width: 'fit-content' }} data-testid={EXPERIMENTS_TEST_IDS.pricingGalleryCardTitle(row.id)}>
                  {row.label}
                </Typography>
              </Tooltip>
              <Typography variant="caption" color="text.secondary">{row.menuLine}</Typography>
              <Typography variant="h6" component="p" sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>
                {usd(row.p50)}{' '}
                <Typography variant="caption" color="text.secondary" component="span">
                  per paywall user
                </Typography>
              </Typography>
              {/* Review B-2: the band is one token — its own line, never split mid-range. */}
              <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', mt: -0.5 }}>
                band {usd(row.p10)}–{usd(row.p90)} · P10–P90
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.cashTiming} · {row.isReference
                  ? 'reference template'
                  : `beats the classic in ${pctWhole(row.probBetterThanReference ?? 0)} of simulated draws`}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>Pick when: {row.pickWhen}</Typography>
              <Box sx={{ mt: 'auto', pt: 0.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={isPicked ? <CheckIcon fontSize="small" /> : undefined}
                  data-testid={EXPERIMENTS_TEST_IDS.pricingGalleryPick(row.id)}
                  onClick={() => setPicked(row.id)}
                  aria-pressed={isPicked}
                >
                  {isPicked ? (picked === null ? 'Picked by default' : 'Picked') : 'Pick'}
                </Button>
              </Box>
            </Box>
          );
        })}
      </Box>
      <Typography variant="subtitle2" component="h3" sx={{ mb: 1, mt: 1 }}>
        Measurement plan at {trafficLabel} starts / day · {pickedRow.label}
      </Typography>
      <Box sx={{ overflowX: 'auto', mb: 2 }}>
        <Table size="small" aria-label="Measurement plan" data-testid={EXPERIMENTS_TEST_IDS.pricingGalleryPlan}>
          <TableHead>
            <TableRow>
              <TableCell>Decision</TableCell>
              <TableCell>What must accumulate</TableCell>
              <TableCell align="right">Expected day (from launch)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plan.map(row => (
              <TableRow key={row.key} data-testid={EXPERIMENTS_TEST_IDS.pricingGalleryPlanRow(row.key)}>
                <TableCell>{row.decision}</TableCell>
                <TableCell>{row.accumulate}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{planDate(row)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        each day = the accumulation at the expected traffic + 1 day of warehouse lag; the band runs from the fastest (P90) to the slowest (P10) chain prior; annual renewals never scale with traffic
      </Typography>

      {/* The END: one contained primary; the calculator handoff is the secondary. */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        {registryHasProducts ? (
          <Button
            variant="contained"
            component={RouterLink}
            to={productsHref}
            data-testid={EXPERIMENTS_TEST_IDS.pricingGalleryCreate}
          >
            Open paywall products →
          </Button>
        ) : (
          /* AC-032.1: no navigation — the confirm dialog opens with the picked
             template's rows prefilled; nothing is created before it is confirmed. */
          <Button
            variant="contained"
            data-testid={EXPERIMENTS_TEST_IDS.pricingGalleryCreate}
            onClick={() => onCreateProducts(
              galleryTemplatePrefill(result, pickedRow),
              CATEGORY_MODIFIERS[inputs.category].label,
            )}
          >
            Create these products in Stripe test
          </Button>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, maxWidth: '80ch' }} data-testid={EXPERIMENTS_TEST_IDS.pricingGalleryCreateNote}>
        {registryHasProducts
          ? `You already have ${registry?.count ?? 0} paywall products — they are the price truth; the templates above rank starting menus on priors only. Model your real prices in the calculator.`
          : `Prices to create for «${pickedRow.label}»: ${pickedRow.menuLine}. The confirm lists every price before anything is created, in Stripe test mode only — names and prices stay editable on the Paywall products page. Creating products is the last step, not the first — nothing is saved here.`}
      </Typography>
    </Paper>
  );
};
